import type { NavigationKey } from "../app/navigation";
import type { RoleDefinition } from "../app/roles";
import { useScenario } from "../simulation/ScenarioContext";

type GuidedWalkthroughProps = {
  onNavigate: (key: NavigationKey) => void;
  role: RoleDefinition;
};

const steps: Array<{
  title: string;
  body: string;
  navigation: NavigationKey;
  action: string;
  dive: "Diagnose" | "Integrate" | "Validate" | "Enable";
}> = [
  {
    title: "Understand the campus promise",
    body: "Review the value proposition, Operating Snapshot, single finals-week story, and next primary action.",
    navigation: "operations",
    action: "Open Operations",
    dive: "Diagnose",
  },
  {
    title: "Diagnose the finals-week outage",
    body: "Start the controlled North Academic scenario and see Wi-Fi, voice, switching, and service status change together.",
    navigation: "operations",
    action: "Simulate Outage",
    dive: "Diagnose",
  },
  {
    title: "Integrate evidence and ownership",
    body: "Acknowledge INC-2048, assign a named owner, and link the change and switch evidence before proposing a response.",
    navigation: "incidents",
    action: "Open Incident Workspace",
    dive: "Integrate",
  },
  {
    title: "Connect infrastructure to human impact",
    body: "Follow CR-SW-04 through NA-214-A1, VLAN 120, TEL-0318, and Priya Nair.",
    navigation: "infrastructure",
    action: "Open Impact Trace",
    dive: "Integrate",
  },
  {
    title: "Validate the human decision",
    body: "Review readiness rules, record required warning overrides, and have a Department Lead approve or reject the response.",
    navigation: "incidents",
    action: "Open Human Gate",
    dive: "Validate",
  },
  {
    title: "Enable verified recovery and handoff",
    body: "Verify service and TEL-0318 recovery, then record the labeled Teams preview, incident report, and shift handoff.",
    navigation: "reports",
    action: "Open Closure Artifacts",
    dive: "Enable",
  },
];

export function GuidedWalkthrough({ onNavigate, role }: GuidedWalkthroughProps) {
  const {
    closeGuide,
    acknowledgedBy,
    assignedOwner,
    guideOpen,
    guideStep,
    humanDecision,
    linkedEvidenceIds,
    nextGuideStep,
    previousGuideStep,
    receipts,
    recoveryVerification,
    selectedTraceNodeId,
    stage,
    startScenario,
  } = useScenario();
  const step = steps[guideStep];
  const reportCreated = receipts.some((receipt) => receipt.kind === "report");
  const completion = [
    true,
    stage !== "calm",
    linkedEvidenceIds.length >= 2 && acknowledgedBy !== null && assignedOwner !== null,
    selectedTraceNodeId !== "INC-2048",
    humanDecision !== null,
    stage === "recovered" && recoveryVerification !== null && reportCreated,
  ];
  const statusDetail = [
    "The hero, Operating Snapshot, Day in the Life story, DIVE banner, and benefits are ready.",
    stage === "calm" ? "The controlled outage has not started." : `Scenario stage: ${stage}.`,
    `${linkedEvidenceIds.length}/2 evidence records · ${acknowledgedBy ? "acknowledged" : "not acknowledged"} · ${assignedOwner ? `owned by ${assignedOwner.name}` : "owner required"}.`,
    selectedTraceNodeId === "INC-2048" ? "Select any downstream infrastructure, endpoint, or person record." : `Trace reached ${selectedTraceNodeId}.`,
    humanDecision ? `Human decision recorded: ${humanDecision.decision}.` : "A Department Lead decision is still required.",
    `${recoveryVerification ? "Recovery verified" : "Recovery verification required"} · ${reportCreated ? "report receipt recorded" : "report receipt required"}.`,
  ];

  if (!guideOpen) return null;

  const handlePrimaryAction = () => {
    onNavigate(step.navigation);
    if (guideStep === 1) startScenario(role.label);
  };

  const handleContinue = () => {
    if (guideStep === steps.length - 1) {
      closeGuide();
      return;
    }
    nextGuideStep();
  };

  return (
    <aside aria-label="Guided CROC-OPS walkthrough" className="walkthrough" role="dialog">
      <header className="walkthrough__header">
        <div>
          <span>DIVE · {step.dive} · Step {guideStep + 1} of {steps.length}</span>
          <strong>{step.title}</strong>
        </div>
        <button aria-label="Close guided walkthrough" className="icon-close" onClick={closeGuide} type="button">×</button>
      </header>
      <div className="walkthrough__progress" aria-label={`${guideStep + 1} of ${steps.length} walkthrough steps`}>
        {steps.map((item, index) => (
          <span
            className={index === guideStep ? "is-current" : completion[index] ? "is-complete" : ""}
            key={item.title}
          />
        ))}
      </div>
      <p>{step.body}</p>
      <div className="walkthrough__state">
        <span>{completion[guideStep] ? "Complete" : "Action required"}</span>
        <small>{statusDetail[guideStep]}</small>
        <small>Current access: {role.label}</small>
      </div>
      <div className="walkthrough__actions">
        <button
          className="button button--secondary"
          disabled={guideStep === 0}
          onClick={previousGuideStep}
          type="button"
        >
          Back
        </button>
        <button className="button button--secondary" onClick={handlePrimaryAction} type="button">
          {step.action}
        </button>
        <button
          className="button button--primary"
          disabled={!completion[guideStep]}
          onClick={handleContinue}
          type="button"
        >
          {guideStep === steps.length - 1 ? "Finish" : "Continue"}
        </button>
      </div>
    </aside>
  );
}
